import { useTranslation } from 'react-i18next';
import { Tray, ArrowSquareOut } from '@phosphor-icons/react';
import type { ArchivedDocumentDto } from '@monorepo/shared';
import { DocumentFileIcon } from './DocumentFileIcon';
import { ParentPathBreadcrumb } from './ParentPathBreadcrumb';

interface ArchivedDocumentsTableProps {
  documents: ArchivedDocumentDto[];
  isLoading: boolean;
  onOpenDocument: (nodeId: string) => void;
  onNavigateToFolder: (folderId: string) => void;
}

const formatDate = (date: Date): string =>
  new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

export const ArchivedDocumentsTable = ({
  documents,
  isLoading,
  onOpenDocument,
  onNavigateToFolder,
}: ArchivedDocumentsTableProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin">
          <Tray size={32} className="text-primary" />
        </div>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Tray size={64} className="text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900">{t('documents.archived_page.empty')}</h3>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
      <table className="w-full min-w-[720px]">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
              {t('documents.archived_page.table.document')}
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
              {t('documents.archived_page.table.location')}
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
              {t('documents.archived_page.table.archived_at')}
            </th>
            <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
              {t('documents.archived_page.table.action')}
            </th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => (
            <tr key={doc.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <DocumentFileIcon mimeType={doc.mimeType} />
                  <span className="text-sm font-medium text-gray-900">{doc.name}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <ParentPathBreadcrumb items={doc.parentPath} onNavigate={onNavigateToFolder} />
              </td>
              <td className="px-4 py-3">
                <span className="text-sm text-gray-600">
                  {doc.archivedAt ? formatDate(doc.archivedAt) : t('documents.parent_path.none')}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => onOpenDocument(doc.id)}
                  className="inline-flex items-center justify-center w-8 h-8 text-gray-600 hover:text-primary hover:bg-primary/10 rounded transition"
                  title={t('documents.archived_page.table.open')}
                >
                  <ArrowSquareOut size={18} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
